const isFaceliftKey = (key: string) =>
	key?.startsWith('ui__') ||
	key?.startsWith('public__') ||
	key?.startsWith('generator__') ||
	key?.startsWith('auth__') ||
	key?.startsWith('dashboard__') ||
	key?.startsWith('api__')

export const transformBackendResponseKey = (key: string) => {
	if (!key) {
		return ''
	}
	if (isFaceliftKey(key)) {
		return key
	}
	return `public__${key.replace(/\./g, '__')}`
}

export const transformTranslationKeys = (
	messages: Record<string, string>,
): Record<string, string> => {
	return {
		...Object.fromEntries(
			Object.entries(messages)
				.filter(([key]) => isFaceliftKey(key))
				.map(([key, value]) => [
					// TODO: remove once BE changed to not prepend public__ to library__ keys
					key
						.replace(/\./g, '__')
						.replace(/^public__dashboard__/g, 'dashboard__'),
					fixTranslationValues(value),
				]),
		),
	}
}

/**
 * Recursively replaces all `.` in object keys with `-`.
 */
// TODO: Move this to BE
const replaceDoubleBrackets = (input: string): string => {
	return input.replace(/{{/g, '{').replace(/}}/g, '}')
}

const convertPluralObjectToICU = (
	pluralObj: Record<string, string>,
): string => {
	const hasExplicitZero = '=0' in pluralObj || 'zero' in pluralObj

	const pluralCases = Object.entries(pluralObj)
		.map(([rule, message]) => `${rule} {${replaceDoubleBrackets(message)}}`)
		.join(' ')

	// CLDR v42+ classifies 0 under "one" for Portuguese/French/etc., but users
	// expect the "other" (plural) form for zero. Add an explicit =0 so ICU
	// MessageFormat picks the plural wording for count 0.
	const hasManyForm = 'many' in pluralObj
	const zeroCasePrefix =
		!hasExplicitZero && !hasManyForm && pluralObj['other']
			? `=0 {${replaceDoubleBrackets(pluralObj['other'])}} `
			: ''

	return `{count, plural, ${zeroCasePrefix}${pluralCases}}`
}
const injectExplicitZeroCase = (icuMessage: string): string => {
	if (/=0\s*\{/.test(icuMessage) || /zero\s*\{/.test(icuMessage)) {
		return icuMessage
	}

	if (/many\s*\{/.test(icuMessage)) {
		return icuMessage
	}

	const otherMatch = icuMessage.match(/other\s*\{((?:[^{}]|\{[^{}]*\})*)\}/)
	if (!otherMatch) return icuMessage

	return icuMessage.replace(
		'{count, plural, ',
		`{count, plural, =0 {${otherMatch[1]}} `,
	)
}

const fixTranslationValues = (message: string): string => {
	if (typeof message !== 'string' && typeof message === 'object') {
		return convertPluralObjectToICU(message)
	}

	if (message.includes(', plural')) {
		let result = message.replace(/^{[^,]+, plural/, '{count, plural')
		result = injectExplicitZeroCase(result)
		return result
	}

	return replaceDoubleBrackets(message)
}

export const renameTranslationKeys = (
	messages: Record<string, string>,
	oldKeyToNewKey: Record<string, string>,
): Record<string, string> =>
	Object.fromEntries(
		Object.entries(messages).map(([key, value]) => [
			oldKeyToNewKey[key] ?? key,
			value,
		]),
	)
